import { contentCategory } from "@hexling/schema";
import { EmptyState, Spinner } from "@hexling/ui";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  type RouterHistory,
} from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Layout } from "./components/Layout";
import { ResetPasswordPage, SignInPage } from "./features/auth/AuthPages";
import { CalendarPage } from "./features/calendar/CalendarPage";
import { GrimoirePage } from "./features/grimoire/GrimoirePage";
import { ListPage } from "./features/grimoire/ListPage";
import { NoteEditorPage } from "./features/grimoire/NoteEditorPage";
import { CategoryPage } from "./features/library/CategoryPage";
import { ItemPage } from "./features/library/ItemPage";
import { LibraryPage } from "./features/library/LibraryPage";
import { CoursePage, LessonPage, LessonsPage } from "./features/lessons/LessonsPages";
import { SettingsPage } from "./features/settings/SettingsPage";
import { TarotPage } from "./features/tarot/TarotPage";
import { TodayPage } from "./features/today/TodayPage";

// Admin is split into its own chunk; most witches never download it.
const AdminPage = lazy(() => import("./features/admin/AdminPage"));
const ReportDetailPage = lazy(() =>
  import("./features/admin/AdminPage").then((m) => ({ default: m.ReportDetailPage })),
);

function NotFound() {
  return (
    <EmptyState glyph="🌫️" title="This page wandered off" action={<Link to="/">Go to Today</Link>}>
      The link may be old, or the page may have moved.
    </EmptyState>
  );
}

const rootRoute = createRootRoute({ component: Layout, notFoundComponent: NotFound });

const route = <P extends string>(path: P, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

const todayRoute = route("/", () => <TodayPage />);
const libraryRoute = route("/library", () => <LibraryPage />);
const tarotRoute = route("/tarot", () => <TarotPage />);
const calendarRoute = route("/calendar", () => <CalendarPage />);
const lessonsRoute = route("/lessons", () => <LessonsPage />);
const settingsRoute = route("/settings", () => <SettingsPage />);
const grimoireRoute = route("/grimoire", () => <GrimoirePage />);

const categoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/library/$category",
  component: function Category() {
    const { category } = categoryRoute.useParams();
    const parsed = contentCategory.safeParse(category);
    return parsed.success ? (
      <CategoryPage key={parsed.data} category={parsed.data} />
    ) : (
      <NotFound />
    );
  },
});

const itemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/library/$category/$slug",
  component: function Item() {
    const { category, slug } = itemRoute.useParams();
    const parsed = contentCategory.safeParse(category);
    return parsed.success ? (
      <ItemPage key={`${category}/${slug}`} category={parsed.data} slug={slug} />
    ) : (
      <NotFound />
    );
  },
});

const courseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lessons/$course",
  component: function Course() {
    return <CoursePage course={courseRoute.useParams().course} />;
  },
});

const lessonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lessons/$course/$lesson",
  component: function Lesson() {
    const { course, lesson } = lessonRoute.useParams();
    return <LessonPage key={lesson} course={course} lesson={lesson} />;
  },
});

const noteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/grimoire/notes/$id",
  validateSearch: (s: Record<string, unknown>): { ref?: string; saved?: number } => ({
    ...(typeof s.ref === "string" ? { ref: s.ref } : {}),
    ...(s.saved ? { saved: 1 } : {}),
  }),
  component: function Note() {
    const { id } = noteRoute.useParams();
    const { ref, saved } = noteRoute.useSearch();
    return <NoteEditorPage key={id} id={id} startRef={ref} justSaved={saved === 1} />;
  },
});

const listRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/grimoire/lists/$id",
  component: function List() {
    return <ListPage id={listRoute.useParams().id} />;
  },
});

const signInRoute = route("/sign-in", () => <SignInPage />);

const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: (s: Record<string, unknown>): { token?: string } =>
    typeof s.token === "string" ? { token: s.token } : {},
  component: function Reset() {
    return <ResetPasswordPage token={resetRoute.useSearch().token} />;
  },
});

const adminRoute = route("/admin", () => (
  <Suspense fallback={<Spinner label="Loading admin" />}>
    <AdminPage />
  </Suspense>
));

const reportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/reports/$id",
  component: function Report() {
    return (
      <Suspense fallback={<Spinner label="Loading report" />}>
        <ReportDetailPage id={reportRoute.useParams().id} />
      </Suspense>
    );
  },
});

const routeTree = rootRoute.addChildren([
  todayRoute,
  libraryRoute,
  categoryRoute,
  itemRoute,
  tarotRoute,
  calendarRoute,
  lessonsRoute,
  courseRoute,
  lessonRoute,
  grimoireRoute,
  noteRoute,
  listRoute,
  settingsRoute,
  signInRoute,
  resetRoute,
  adminRoute,
  reportRoute,
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({ routeTree, history, defaultPreload: "intent", scrollRestoration: true });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
